import type { Device } from 'src/types/Device';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { compareFWDates, getDeviceFirmwareString } from 'src/helpers/firmware_compare_helpers';

/** Minimum firmware build (YYYYMMDD-HHMMSS) that supports the `video.tint` setting. */
const VIDEO_TINT_MIN_FW = '20260601-205051';

/** Minimum firmware build (YYYYMMDD-HHMMSS) that supports the `video.temperature` setting. */
const VIDEO_TEMPERATURE_MIN_FW = '20260603';

/**
 * Whether the device firmware is at least `minFw`: it must be either the exact
 * introducing build or a build from a later date.
 */
const isMinFirmware = (device: Device, minFw: string): boolean => {
	const fwId = getDeviceFirmwareString(
		device,
		readDeviceSettings(device.id),
		readDeviceStatus(device.id),
	);
	if (!fwId) return false;

	return fwId.includes(minFw) || compareFWDates(fwId, '>', minFw.split('-')[0]);
};

/**
 * Whether the device firmware supports the camera `video.tint` setting.
 *
 * @ToDo: Remove checks after device released. This is for pre-release versions.
 */
export const isVideoTintSupported = (device: Device): boolean =>
	isMinFirmware(device, VIDEO_TINT_MIN_FW);

/**
 * Whether the device firmware supports the camera `video.temperature` (color
 * temperature) setting.
 *
 * @ToDo: Remove checks after device released. This is for pre-release versions.
 */
export const isVideoTemperatureSupported = (device: Device): boolean =>
	isMinFirmware(device, VIDEO_TEMPERATURE_MIN_FW);
